/** @jsx jsx */
import React, { useCallback } from 'react';
import { css, jsx } from '@compiled/react';
import Heading from '@atlaskit/heading';
import CalendarIcon from '@atlaskit/icon/core/calendar';
import CheckCircleIcon from '@atlaskit/icon/core/check-circle';
import EditBulkIcon from '@atlaskit/icon/core/edit-bulk';
import TasksIcon from '@atlaskit/icon/core/tasks';
import type { NewGlyphColorProps } from '@atlaskit/icon/types';
import { Box, Flex, Text, xcss } from '@atlaskit/primitives';
import { token } from '@atlaskit/tokens';
// TODO HL-1430 move this constant into our package once JWM business page is replaced with ours
import {
	DONE_THIS_WEEK_JQL,
	UPDATED_THIS_WEEK_JQL,
	CREATED_THIS_WEEK_JQL,
	DUE_THIS_WEEK_JQL,
} from '@atlassian/jira-business-summary-services/src/services/constants.tsx';
import { FormattedMessage } from '@atlassian/jira-intl';
import { Link } from '@atlassian/react-resource-router';
import {
	useSummaryFeatures,
	type LinkResolverOptions,
} from '../../../controllers/features-context/index.tsx';
import {
	overviewCountBoundaryIds,
	SectionIds,
	type OverviewCountType,
} from '../../../common/types.tsx';
import { useSectionAnalytics } from '../../../common/ui/analytics/index.tsx';
import messages from './messages.tsx';

type OverviewCountProps = {
	data: { count: number; type: OverviewCountType };
};

type IconTileStyles = ReturnType<typeof xcss>;
type IconColor = NewGlyphColorProps['color'];
const ICON_CONFIG_FOR_TYPE: Record<
	OverviewCountType,
	[typeof CalendarIcon, IconColor, IconTileStyles?]
> = {
	done: [
		CheckCircleIcon,
		token('color.icon.accent.lime'),
		xcss({ backgroundColor: 'color.background.accent.lime.subtlest' }),
	],
	updated: [
		EditBulkIcon,
		token('color.icon.subtle'),
		xcss({ backgroundColor: 'elevation.surface.sunken' }),
	],
	created: [
		TasksIcon,
		token('color.icon.subtle'),
		xcss({ backgroundColor: 'elevation.surface.sunken' }),
	],
	due: [
		CalendarIcon,
		token('color.icon.accent.red'),
		xcss({ backgroundColor: 'color.background.accent.red.subtlest' }),
	],
};

const ICON_CONFIG_FOR_EMPTY: [IconColor, IconTileStyles] = [
	token('color.icon.subtle'),
	xcss({ backgroundColor: 'elevation.surface.sunken' }),
];

const REDIRECT_URL_OPTIONS_FOR_TYPE: Record<OverviewCountType, LinkResolverOptions> = {
	done: {
		filter: DONE_THIS_WEEK_JQL,
	},
	updated: { filter: UPDATED_THIS_WEEK_JQL },
	created: { filter: CREATED_THIS_WEEK_JQL },
	due: {
		filter: DUE_THIS_WEEK_JQL,
	},
};

const HEADING_MESSAGE_ID_FOR_TYPE: Record<OverviewCountType, keyof typeof messages> = {
	done: 'doneTitle',
	updated: 'updatedTitle',
	created: 'createdTitle',
	due: 'dueTitle',
};

export const OverviewCount = ({ data: { count, type } }: OverviewCountProps) => {
	const [IconComponent, ...iconConfigsForType] = ICON_CONFIG_FOR_TYPE[type];
	const [iconColor, iconTileStyles] = count > 0 ? iconConfigsForType : ICON_CONFIG_FOR_EMPTY;
	const { fireOverviewCardAnalytics } = useSectionAnalytics(
		SectionIds.OverviewCard,
		overviewCountBoundaryIds[type],
	);

	const onLinkClick = useCallback(() => {
		fireOverviewCardAnalytics('viewWithFilter');
	}, [fireOverviewCardAnalytics]);

	const { linksResolver, linksTarget } = useSummaryFeatures();
	const onClickHref = linksResolver(REDIRECT_URL_OPTIONS_FOR_TYPE[type]);

	return (
		<Link href={onClickHref} target={linksTarget} css={linkStyles} onClick={onLinkClick}>
			<Flex direction="row" gap="space.100" xcss={containerStyles}>
				<Flex
					justifyContent="center"
					alignItems="center"
					// eslint-disable-next-line @atlaskit/design-system/consistent-css-prop-usage
					xcss={[iconContainerStyles, iconTileStyles]}
				>
					<IconComponent spacing="spacious" label="" color={iconColor} />
				</Flex>

				<Flex alignItems="center" xcss={contentContainerStyles}>
					<Box>
						<FormattedMessage
							{...messages[HEADING_MESSAGE_ID_FOR_TYPE[type]]}
							values={{
								count,
								header: (chunks) => <Heading size="small">{chunks}</Heading>,
								copy: (chunks) => (
									<Text size="UNSAFE_small" color="color.text.subtlest">
										{chunks}
									</Text>
								),
							}}
						/>
					</Box>
				</Flex>
			</Flex>
		</Link>
	);
};

const linkStyles = css({
	'&:hover': {
		textDecoration: 'none',
	},
});

const containerStyles = xcss({
	margin: 'space.negative.050',
	padding: 'space.150',
	height: '64px',
});

const contentContainerStyles = xcss({
	height: '100%',
});

const iconContainerStyles = xcss({
	height: '40px',
	width: '40px',
	borderRadius: '4px',
});
